// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { useFlag } from '@calendly/feature-check-react';
import { usePageTitle } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { Badge } from '@calendly/ui/components/badge';
import { QuestionCircleIcon } from '@calendly/ui/components/icons';
import { PageHeader } from '@calendly/ui/components/page-header';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { EMPTY_STATE_TYPES, EmptyState } from 'components/empty_state';
import { LearnMoreButton } from 'components/page_elements';
import { currentStageSelector } from 'features/billing';
import { useContextualHelp } from 'features/contextual_help';
import { CONTENT_PAGES, useNewEmptyStates } from 'features/empty_state';
import { useIsSettingsModalLayout } from 'features/settings_modal/layout_context';
import { UpgradeBanner } from 'features/upgrade_banner';
import { Content } from './content';
import { ExperimentContent } from './experiment_content';
import { useExperimentEmptyState } from './hooks';
const dashboardContainerCss = "dx5nc3n";
const DashboardContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(dashboardContainerCss, props.className)} />;
});
export const Dashboard = () => {
  usePageTitle(I18n.t('publisher.admin.dashboard.title'));
  const inSettingsModal = useIsSettingsModalLayout();
  const isNewBadgeHidden = useFlag('remove_new_flags_comp');
  const isNewEmptyStateEnabled = useNewEmptyStates();
  const experimentalEmptyStateUsed = useExperimentEmptyState();
  const currentStage = useSelector(currentStageSelector);
  const stageLoadedNotTrial = !!currentStage && currentStage !== 'trial';
  const {
    open: openContextualHelp
  } = useContextualHelp();
  const onClickLearnMore = () => {
    openContextualHelp({
      contentKey: CONTENT_PAGES.adminDashboard
    });
  };
  const useExperimentalEmptyState = isNewEmptyStateEnabled && experimentalEmptyStateUsed;
  const showUpgradeBanner = isNewEmptyStateEnabled && stageLoadedNotTrial;
  const Container = useExperimentalEmptyState ? EmptyState : DashboardContainer;
  const containerProps = useExperimentalEmptyState ? {
    type: EMPTY_STATE_TYPES.preview,
    contentKey: CONTENT_PAGES.adminDashboard,
    featureKey: CONTENT_PAGES.adminDashboard
  } : {};
  const ContentComponent = isNewEmptyStateEnabled ? ExperimentContent : Content;
  const pageHeader = <PageHeader title={I18n.t('publisher.admin.dashboard.title')} {...!isNewEmptyStateEnabled && {
    description: I18n.t('publisher.admin.dashboard.description')
  }} {...!isNewEmptyStateEnabled && !isNewBadgeHidden && {
    badge: <Badge type="new">{I18n.t('publisher.components.badge.new')}</Badge>
  }} {...isNewEmptyStateEnabled && {
    protip: <LearnMoreButton onClick={onClickLearnMore}>
            <QuestionCircleIcon />
          </LearnMoreButton>
  }} />;
  return <>
      {!inSettingsModal && pageHeader}
      {/* UpgradeBanner for both new empty state flows */}
      {showUpgradeBanner && <UpgradeBanner featureKey="admin_dashboard" {...experimentalEmptyStateUsed && {
      contentKey: CONTENT_PAGES.adminDashboard
    }} />}
      <Container {...containerProps}>
        <ContentComponent />
      </Container>
    </>;
};

require("./dashboard.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./dashboard.js");